import { useContext, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import CircularProgress from '@mui/material/CircularProgress'
import { ToastNotificationContext } from '../../../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../../../global/utils/fetch'
import { putTireDeliveryData } from '../api'
import Table from '../../../../../global/components/table/Table'
import { Column } from 'react-table'
import { useNavigate } from 'react-router-dom'
import { WoDetailContext } from '../../../../../work-orders/components/context/context'
import WorkOrderDetailModal from '../../../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import TableTopper from '../../../../../global/components/table/helpers/table-topper/TableTopper'
import TableTitle from '../../../../../global/components/table/helpers/table-title/TableTitle'
import { Button } from '@mui/material'

interface successfulTireRackOrders {
  tireRackOrderNumber: string
  orderObjectId: string
}

interface unsuccessfulTireRackOrders {
  tireRackOrderNumber: string
  error: string
}

interface IUploadTireDeliveryModalProps {
  isOpen: boolean
  handleClosed: () => void
}

function UploadTireDeliveryModal(props: IUploadTireDeliveryModalProps) {
  const { isOpen, handleClosed } = props
  const [isFileUploading, setIsFileUploading] = useState(false)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const navigate = useNavigate()
  const [successfulTireRackOrders, setSuccessfulTireRackOrders] = useState<
    successfulTireRackOrders[]
  >([])
  const [unsuccessfulTireRackOrders, setUnsuccessfulTireRackOrders] = useState<
    unsuccessfulTireRackOrders[]
  >([])
  const { queryOrderData, clearData, data } = useContext(WoDetailContext)
  const [workOrderDetailsModalOpen, setWorkOrderDetailsModalOpen] =
    useState(false)

  const title = 'Upload Tire Rack Delivery Data'
  const description =
    'Please upload the Tire Rack CSV for orders marked for delivery here.'

  // Handle opening the work order detail modal
  async function handleOpenWorkOrder(orderObjectId: string) {
    const url = `/reports/tire-delivery/${orderObjectId}`
    navigate(url)
    await queryOrderData(orderObjectId)
    setWorkOrderDetailsModalOpen(true)
  }

  // Handle closing the work order detail modal
  function handleCloseWorkOrder() {
    navigate('/reports')
    setWorkOrderDetailsModalOpen(false)
    clearData()
  }

  async function handleUploadFileInput(e: any) {
    setIsFileUploading(true)
    try {
      const formData = new FormData()
      const file = e.target.files[0]
      formData.append('file', file)
      const tireDeliveryReponse = await fetchData<{
        successfulTireRackOrders: successfulTireRackOrders[]
        unsuccessfulTireRackOrders: unsuccessfulTireRackOrders[]
      }>(putTireDeliveryData(formData))

      const { successfulTireRackOrders, unsuccessfulTireRackOrders } =
        tireDeliveryReponse

      setSuccessfulTireRackOrders(successfulTireRackOrders)
      setUnsuccessfulTireRackOrders(unsuccessfulTireRackOrders)

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      })
    } catch (error: any) {
      console.error(error)

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message ?? '',
        severity: 'error',
      })
    } finally {
      e.target.value = null
      setIsFileUploading(false)
    }
  }

  function prepareResultsData() {
    const successful = successfulTireRackOrders.map((item) => ({
      tireRackOrderNumber: item.tireRackOrderNumber,
      isSuccessful: true,
      orderObjectId: item.orderObjectId,
      error: null,
    }))

    const unsuccessful = unsuccessfulTireRackOrders.map((item) => ({
      tireRackOrderNumber: item.tireRackOrderNumber,
      isSuccessful: false,
      orderObjectId: null,
      error: item.error,
    }))

    return [...successful, ...unsuccessful]
  }

  // Combined columns for all tire rack orders
  const combinedOrdersColumns: Column<any>[] = [
    {
      Header: 'TR Order Number',
      accessor: 'tireRackOrderNumber',
    },
    {
      Header: 'Status',
      Cell: ({ row }: any) => (
        <span
          style={{
            color: row.original.isSuccessful ? 'green' : 'red',
          }}
        >
          {row.original.isSuccessful ? 'Success' : 'Failed'}
        </span>
      ),
    },
    {
      Header: 'Action / Error',
      Cell: ({ row }: any) =>
        row.original.isSuccessful ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => handleOpenWorkOrder(row.original.orderObjectId)}
              variant='outlined'
              size={'small'}
              color={'inherit'}
              style={{ textTransform: 'none' }}
              sx={{
                color: '#D70000',
                '&:hover': {
                  backgroundColor: '#d69090',
                },
              }}
            >
              {'Open Order'}
            </Button>
          </div>
        ) : (
          <span>{row.original.error}</span>
        ),
    },
  ]

  return (
    <div>
      <Modal
        open={isOpen}
        onClose={() => {
          // we will prompt the user to confirm if they merely click out of the modal as opposed to clicking the close button
          if (!window.confirm('You are leaving the upload page. Continue?')) {
            return
          }
          handleClosed()
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box className={styles.uploadModalContainer}>
          <div className={styles.title}>{title}</div>
          <div className={styles.contentContainer}>
            <div>{description}</div>
            <input
              style={{ display: 'none' }}
              id='fileUpload'
              onChange={handleUploadFileInput}
              type='file'
              accept='.csv'
            />
            <label
              htmlFor='fileUpload'
              style={{ whiteSpace: 'nowrap' }}
              className={styles.uploadButton}
            >
              Upload File
            </label>
          </div>
          {isFileUploading && <CircularProgress size={20} />}
          {(successfulTireRackOrders.length > 0 ||
            unsuccessfulTireRackOrders.length > 0) && (
            <div style={{ width: '100%', overflowY: 'scroll' }}>
              <TableTopper>
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    flexDirection: 'column',
                  }}
                >
                  <TableTitle>
                    <>
                      Upload Results ({successfulTireRackOrders.length}/
                      {successfulTireRackOrders.length +
                        unsuccessfulTireRackOrders.length}
                      )
                    </>
                  </TableTitle>
                </div>
              </TableTopper>
              <Table
                columns={combinedOrdersColumns}
                data={prepareResultsData()}
                isDisplayMode={true}
              />
            </div>
          )}
          <PrimaryButton
            onClick={handleClosed}
            buttonName={'Close'}
            style={{ marginTop: '20px' }}
            disabled={isFileUploading}
          />
        </Box>
      </Modal>
      <WorkOrderDetailModal
        isOpen={workOrderDetailsModalOpen && !!data}
        handleClosed={handleCloseWorkOrder}
      />
    </div>
  )
}

export default UploadTireDeliveryModal
